import React, { useState, useEffect } from 'react'
import {
  Box,
  Typography,
  Paper,
  Table,
  TableBody,
  TableCell,
  TableContainer,
  TableHead,
  TableRow,
  TextField,
  FormControl,
  InputLabel,
  Select,
  MenuItem,
  Button,
  IconButton,
  Grid,
  Chip,
  Alert
} from '@mui/material'
import {
  Add as AddIcon,
  Delete as DeleteIcon,
  Edit as EditIcon,
  Save as SaveIcon,
  Cancel as CancelIcon
} from '@mui/icons-material'
import { billingApi } from '../../services/api'
import { nomenclatorV2Api } from '../../services/nomenclatorV2Api'
import { ContractSegmentPricing, ContractSegmentPricingCreate } from '../../types/api'
import toast from 'react-hot-toast'

interface SegmentDefinition {
  id: string
  code: string
  display_name: string
  display_name_ro?: string
  region_id: string
  modality: string
}

interface ContractSegmentPricingProps {
  contractId: string
  currency: string
  onPricingChange?: (pricing: ContractSegmentPricing[]) => void
}

interface NewPricingForm {
  segment_definition_id: string
  price: string
  effective_from: string
  effective_to?: string
}

const ContractSegmentPricingComponent: React.FC<ContractSegmentPricingProps> = ({
  contractId,
  currency,
  onPricingChange
}) => {
  const [pricing, setPricing] = useState<ContractSegmentPricing[]>([])
  const [nomenclator, setNomenclator] = useState<SegmentDefinition[]>([])
  const [loading, setLoading] = useState(false)
  const [editingId, setEditingId] = useState<string | null>(null)
  const [editValues, setEditValues] = useState<{ price: string; effective_from: string; effective_to?: string }>({
    price: '',
    effective_from: '',
    effective_to: ''
  })

  // Form for adding new pricing
  const [newPricing, setNewPricing] = useState<NewPricingForm>({
    segment_definition_id: '',
    price: '',
    effective_from: new Date().toISOString().split('T')[0],
    effective_to: ''
  })
  
  // Search term for filtering body parts
  const [searchTerm, setSearchTerm] = useState('')

  // Load nomenclator v2 items
  const loadNomenclator = async () => {
    try {
      const data = await nomenclatorV2Api.listSegmentDefinitions()
      setNomenclator(data)
    } catch (error) {
      console.error('Error loading nomenclator v2:', error)
      toast.error('Eroare la încărcarea nomenclatorului v2')
    }
  }

  // Load existing pricing
  const loadPricing = async () => {
    if (!contractId) return
    
    try {
      setLoading(true)
      const pricingData = await billingApi.getContractSegmentPricing(contractId)
      setPricing(pricingData)
      onPricingChange?.(pricingData)
    } catch (error) {
      console.error('Error loading pricing:', error)
      toast.error('Eroare la încărcarea prețurilor')
    } finally {
      setLoading(false)
    }
  }

  useEffect(() => {
    loadNomenclator()
  }, [])

  useEffect(() => {
    if (contractId) {
      loadPricing()
    }
  }, [contractId])

  // Add new pricing
  const handleAddPricing = async () => {
    if (!newPricing.segment_definition_id || !newPricing.price) {
      toast.error('Segment și preț sunt obligatorii')
      return
    }

    // Check if segment already exists
    const existingPricing = pricing.find(p => p.segment_definition_id === newPricing.segment_definition_id && p.is_active)
    if (existingPricing) {
      toast.error('Acest segment are deja un preț activ')
      return
    }

    try {
      setLoading(true)
      const pricingData: ContractSegmentPricingCreate = {
        segment_definition_id: newPricing.segment_definition_id,
        price: parseFloat(newPricing.price),
        effective_from: newPricing.effective_from,
        effective_to: newPricing.effective_to || undefined,
        is_active: true
      }

      const newPricingItem = await billingApi.createContractSegmentPricing(contractId, pricingData)
      setPricing(prev => [...prev, newPricingItem])
      
      // Reset form
      setNewPricing({
        segment_definition_id: '',
        price: '',
        effective_from: new Date().toISOString().split('T')[0],
        effective_to: ''
      })
      
      toast.success('Preț adăugat cu succes')
      onPricingChange?.([...pricing, newPricingItem])
    } catch (error: any) {
      console.error('Error adding pricing:', error)
      toast.error(error.response?.data?.detail || 'Eroare la adăugarea prețului')
    } finally {
      setLoading(false)
    }
  }

  // Start editing
  const handleStartEdit = (item: ContractSegmentPricing) => {
    setEditingId(item.id)
    setEditValues({
      price: item.price.toString(),
      effective_from: item.effective_from,
      effective_to: item.effective_to || ''
    })
  }

  // Cancel editing
  const handleCancelEdit = () => {
    setEditingId(null)
    setEditValues({ price: '', effective_from: '', effective_to: '' })
  }

  // Save edit
  const handleSaveEdit = async (itemId: string) => {
    if (!editValues.price || !editValues.effective_from) {
      toast.error('Prețul și data sunt obligatorii')
      return
    }

    try {
      setLoading(true)
      const updateData = {
        price: parseFloat(editValues.price),
        effective_from: editValues.effective_from,
        effective_to: editValues.effective_to || undefined
      }

      const updatedPricing = await billingApi.updateContractSegmentPricing(contractId, itemId, updateData)
      
      // Update local state
      setPricing(prev => prev.map(item => 
        item.id === itemId ? updatedPricing : item
      ))
      
      handleCancelEdit()
      toast.success('Preț actualizat cu succes')
      onPricingChange?.(pricing.map(item => item.id === itemId ? updatedPricing : item))
    } catch (error: any) {
      console.error('Error updating pricing:', error)
      toast.error(error.response?.data?.detail || 'Eroare la actualizarea prețului')
    } finally {
      setLoading(false)
    }
  }

  // Delete pricing (soft delete by setting is_active = false)
  const handleDeletePricing = async (itemId: string) => {
    if (!confirm('Sigur doriți să ștergeți acest preț?')) return

    try {
      setLoading(true)
      await billingApi.deleteContractSegmentPricing(contractId, itemId)
      
      // Update local state - remove the item completely from the list
      setPricing(prev => prev.filter(item => item.id !== itemId))
      
      toast.success('Preț șters cu succes')
      const updatedPricing = pricing.filter(item => item.id !== itemId)
      onPricingChange?.(updatedPricing)
    } catch (error: any) {
      console.error('Error deleting pricing:', error)
      toast.error(error.response?.data?.detail || 'Eroare la ștergerea prețului')
    } finally {
      setLoading(false)
    }
  }

  // Get nomenclator item by id
  const getNomenclatorItem = (id: string) => {
    return nomenclator.find(item => item.id === id)
  }

  // Get available segments (not already priced and filtered by search)
  const getAvailableSegments = () => {
    const usedIds = pricing.filter(p => p.is_active).map(p => p.segment_definition_id)
    let available = nomenclator.filter(item => !usedIds.includes(item.id))
    
    // Apply search filter
    if (searchTerm) {
      const searchUpper = searchTerm.toUpperCase()
      available = available.filter(item =>
        (item.display_name_ro || item.display_name).toUpperCase().includes(searchUpper) ||
        item.code.toUpperCase().includes(searchUpper) ||
        item.modality.toUpperCase().includes(searchUpper)
      )
    }
    
    return available
  }

  if (loading && pricing.length === 0) {
    return (
      <Box sx={{ p: 2 }}>
        <Typography>Încărcare prețuri...</Typography>
      </Box>
    )
  }

  return (
    <Box sx={{ mt: 3 }}>
      <Typography variant="h6" gutterBottom>
        Prețuri per Segment
      </Typography>

      {/* Add new pricing form */}
      <Paper sx={{ p: 2, mb: 2 }}>
        <Typography variant="subtitle1" gutterBottom>
          Adaugă preț nou
        </Typography>
        <Grid container spacing={2} alignItems="center">
          <Grid item xs={12} md={4}>
            <FormControl fullWidth size="small">
              <InputLabel>Segment</InputLabel>
              <Select
                value={newPricing.segment_definition_id}
                onChange={(e) => setNewPricing({ ...newPricing, segment_definition_id: e.target.value })}
                label="Segment"
                onClose={() => setSearchTerm('')}
                MenuProps={{
                  autoFocus: false,
                  PaperProps: {
                    sx: { maxHeight: 400 }
                  }
                }}
              >
                <MenuItem
                  disableRipple
                  onKeyDown={(e) => e.stopPropagation()}
                  sx={{ 
                    '&:hover': { backgroundColor: 'transparent' },
                    cursor: 'default'
                  }}
                >
                  <TextField
                    size="small"
                    placeholder="Caută segment..."
                    fullWidth
                    autoFocus
                    value={searchTerm}
                    onChange={(e) => setSearchTerm(e.target.value)}
                    onClick={(e) => e.stopPropagation()}
                    onKeyDown={(e) => {
                      e.stopPropagation()
                    }}
                  />
                </MenuItem>
                {getAvailableSegments().length === 0 ? (
                  <MenuItem disabled>
                    <Typography variant="body2" color="text.secondary">
                      {searchTerm ? 'Nu s-au găsit rezultate' : 'Nu sunt segmente disponibile'}
                    </Typography>
                  </MenuItem>
                ) : (
                  getAvailableSegments().map((item) => (
                    <MenuItem key={item.id} value={item.id}>
                      <Box>
                        <Typography variant="body2" fontWeight="medium">
                          {item.display_name_ro || item.display_name}
                        </Typography>
                        <Typography variant="caption" color="text.secondary">
                          {item.code} ({item.modality})
                        </Typography>
                      </Box>
                    </MenuItem>
                  ))
                )}
              </Select>
            </FormControl>
          </Grid>
          
          <Grid item xs={12} md={2}>
            <TextField
              fullWidth
              size="small"
              label={`Preț (${currency})`}
              type="number"
              value={newPricing.price}
              onChange={(e) => setNewPricing({ ...newPricing, price: e.target.value })}
              inputProps={{ min: 0, step: 0.01 }}
            />
          </Grid>
          
          <Grid item xs={12} md={2}>
            <TextField
              fullWidth
              size="small"
              label="Data început"
              type="date"
              value={newPricing.effective_from}
              onChange={(e) => setNewPricing({ ...newPricing, effective_from: e.target.value })}
              InputLabelProps={{ shrink: true }}
            />
          </Grid>
          
          <Grid item xs={12} md={2}>
            <TextField
              fullWidth
              size="small"
              label="Data sfârșit"
              type="date"
              value={newPricing.effective_to}
              onChange={(e) => setNewPricing({ ...newPricing, effective_to: e.target.value })}
              InputLabelProps={{ shrink: true }}
            />
          </Grid>
          
          <Grid item xs={12} md={2}>
            <Button
              fullWidth
              variant="contained"
              startIcon={<AddIcon />}
              onClick={handleAddPricing}
              disabled={loading || !newPricing.segment_definition_id || !newPricing.price}
            >
              Adaugă
            </Button>
          </Grid>
        </Grid>
      </Paper>

      {/* Existing pricing table */}
      {pricing.filter(item => item.is_active).length > 0 ? (
        <TableContainer component={Paper}>
          <Table size="small">
            <TableHead>
              <TableRow>
                <TableCell>Segment</TableCell>
                <TableCell>Modalitate</TableCell>
                <TableCell align="right">Preț ({currency})</TableCell>
                <TableCell>Valabilitate</TableCell>
                <TableCell>Status</TableCell>
                <TableCell align="right">Acțiuni</TableCell>
              </TableRow>
            </TableHead>
            <TableBody>
              {pricing.filter(item => item.is_active).map((item) => {
                const nomenclatorItem = getNomenclatorItem(item.segment_definition_id)
                const isEditing = editingId === item.id
                
                // ✨ Use display_name from API (if available) or fallback to nomenclator lookup
                const displayName = (item as any).display_name || nomenclatorItem?.display_name_ro || nomenclatorItem?.display_name || 'N/A'
                const code = (item as any).code || nomenclatorItem?.code || 'N/A'
                const modality = (item as any).modality || nomenclatorItem?.modality || 'N/A'
                
                return (
                  <TableRow key={item.id}>
                    <TableCell>
                      <Typography variant="body2" fontWeight="medium">
                        {displayName}
                      </Typography>
                      <Typography variant="caption" color="text.secondary">
                        {code}
                      </Typography>
                    </TableCell>
                    <TableCell>
                      <Chip
                        label={modality}
                        size="small"
                        variant="outlined"
                      />
                    </TableCell>
                    <TableCell align="right">
                      {isEditing ? (
                        <TextField
                          size="small"
                          type="number"
                          value={editValues.price}
                          onChange={(e) => setEditValues({ ...editValues, price: e.target.value })}
                          inputProps={{ min: 0, step: 0.01 }}
                          sx={{ width: 100 }}
                        />
                      ) : (
                        <Typography variant="body2" fontWeight="medium">
                          {typeof item.price === 'number' ? item.price.toFixed(2) : 
                           (isNaN(parseFloat(item.price)) ? '0.00' : parseFloat(item.price).toFixed(2))}
                        </Typography>
                      )}
                    </TableCell>
                    <TableCell>
                      {isEditing ? (
                        <Box sx={{ display: 'flex', gap: 1, flexDirection: 'column' }}>
                          <TextField
                            size="small"
                            type="date"
                            label="De la"
                            value={editValues.effective_from}
                            onChange={(e) => setEditValues({ ...editValues, effective_from: e.target.value })}
                            InputLabelProps={{ shrink: true }}
                          />
                          <TextField
                            size="small"
                            type="date"
                            label="Până la"
                            value={editValues.effective_to}
                            onChange={(e) => setEditValues({ ...editValues, effective_to: e.target.value })}
                            InputLabelProps={{ shrink: true }}
                          />
                        </Box>
                      ) : (
                        <Typography variant="body2">
                          {new Date(item.effective_from).toLocaleDateString()}
                          {item.effective_to && (
                            <> - {new Date(item.effective_to).toLocaleDateString()}</>
                          )}
                        </Typography>
                      )}
                    </TableCell>
                    <TableCell>
                      <Chip
                        label={item.is_active ? 'Activ' : 'Inactiv'}
                        color={item.is_active ? 'success' : 'default'}
                        size="small"
                      />
                    </TableCell>
                    <TableCell align="right">
                      {isEditing ? (
                        <>
                          <IconButton
                            size="small"
                            onClick={() => handleSaveEdit(item.id)}
                            color="primary"
                          >
                            <SaveIcon />
                          </IconButton>
                          <IconButton
                            size="small"
                            onClick={handleCancelEdit}
                          >
                            <CancelIcon />
                          </IconButton>
                        </>
                      ) : (
                        <>
                          <IconButton
                            size="small"
                            onClick={() => handleStartEdit(item)}
                          >
                            <EditIcon />
                          </IconButton>
                          <IconButton
                            size="small"
                            onClick={() => handleDeletePricing(item.id)}
                            color="error"
                          >
                            <DeleteIcon />
                          </IconButton>
                        </>
                      )}
                    </TableCell>
                  </TableRow>
                )
              })}
            </TableBody>
          </Table>
        </TableContainer>
      ) : (
        <Alert severity="info">
          Nu există prețuri configurate pentru acest contract. Adăugați primul preț folosind formularul de mai sus.
        </Alert>
      )}
    </Box>
  )
}

export default ContractSegmentPricingComponent
